import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import PropTypes from 'prop-types';
import React, { useEffect, useCallback, useRef } from 'react';

import Chevron from 'components/Chevron';
import IconButton from 'components/IconButton';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import { cn } from 'utils/Tailwind';

import BannerMessage from './BannerMessage';

import '@splidejs/react-splide/css';

const BANNER_HEIGHT = Theme.get('banner-height');

const CAROUSEL_OPTIONS = {
  type: 'fade',
  rewind: true,
  arrows: true,
  pagination: false,
  autoplay: true,
  interval: 8000,
  pauseOnHover: true,
  pauseOnFocus: true,
};

const Banner = ({
  backgroundColor = Theme.get('colors-success'),
  campaignAnalyticsMetadata,
  isSingleMessage = false,
  items = [],
  isLoading = false,
  trackClick = () => {},
}) => {
  const splideRef = useRef(null);

  useEffect(() => {
    if (
      typeof splideRef.current?.pause === 'function' &&
      typeof splideRef.current?.play === 'function'
    ) {
      if (isSingleMessage || isLoading) {
        splideRef.current.pause();
      } else {
        splideRef.current?.play();
      }
    }
  }, [isSingleMessage, isLoading]);

  const onBannerClick = useCallback(
    payload => {
      trackClick(payload, campaignAnalyticsMetadata);
    },
    [trackClick, campaignAnalyticsMetadata]
  );

  return (
    <div
      className={cn(
        'Banner relative w-full transition-opacity duration-300',
        isLoading ? 'opacity-0' : 'opacity-100'
      )}
      style={{ backgroundColor, height: `${BANNER_HEIGHT}px` }}
      data-testid="banner-container"
    >
      <Splide
        className="flex h-full justify-center px-6"
        options={CAROUSEL_OPTIONS}
        hasTrack={false}
        ref={splideRef}
      >
        <CarouselArrows isSingleMessage={isSingleMessage} />

        <SplideTrack>
          {items.map((item, index) => {
            const { message, icon, url } = item;
            const slideId = `banner-slide-${index}`;

            const slideContent = <BannerMessage message={message} icon={icon} />;

            return (
              <SplideSlide key={slideId} data-testid={`banner-slide-${index}`}>
                {url ? (
                  <a
                    className="block h-full cursor-pointer font-medium no-underline"
                    href={url}
                    onClick={() => onBannerClick(documentToHtmlString(message))}
                  >
                    {slideContent}
                  </a>
                ) : (
                  <div
                    className="flex h-full items-center justify-center"
                    onClick={() => onBannerClick(documentToHtmlString(message))}
                  >
                    {slideContent}
                  </div>
                )}
              </SplideSlide>
            );
          })}
        </SplideTrack>
      </Splide>
    </div>
  );
};

function CarouselArrows({ isSingleMessage }) {
  if (isSingleMessage) {
    return null;
  }

  return (
    <div className="splide__arrows absolute right-0 top-0 min-h-[40px] min-w-[40px] md:right-6">
      <button className="splide__arrow splide__arrow--prev !hidden"></button>

      <IconButton
        className={cn('splide__arrow splide__arrow--next !right-auto !h-full !w-full')}
        aria-label={I18n.localize('global.next')}
        data-testid="banner-next-button"
      >
        <Chevron fillColor="colors-white" direction="right" />
      </IconButton>
    </div>
  );
}

CarouselArrows.propTypes = {
  isSingleMessage: PropTypes.bool,
};

CarouselArrows.defaultProps = {
  isSingleMessage: false,
};

Banner.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.shape({}).isRequired,
      icon: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
      url: PropTypes.string,
    })
  ),
  backgroundColor: PropTypes.string,
  campaignAnalyticsMetadata: PropTypes.shape({
    dyDecisionId: PropTypes.string,
    dyVariationId: PropTypes.number,
  }),
  trackClick: PropTypes.func,
  trackArrowClick: PropTypes.func,
  isSingleMessage: PropTypes.bool,
  isLoading: PropTypes.bool,
};

Banner.defaultProps = {
  items: [],
  backgroundColor: Theme.get('colors-success'),
  trackClick: () => {},
  trackArrowClick: () => {},
  isSingleMessage: false,
  isLoading: false,
};

export { BANNER_HEIGHT };
export default Banner;
